// ✅ نفس الاستيرادات بدون تغيير
import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  MapPinIcon,
  CalendarIcon,
  BuildingOfficeIcon,
} from "@heroicons/react/24/outline";
import { useStore } from "@/store/useStore";
import Card from "@/components/ui/Card";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Select from "@/components/ui/Select";

const Careers = () => {
  const { t, i18n } = useTranslation("common");
  const { seedData } = useStore();
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const currentLang = i18n.language || "en";

  // ✅ جلب البيانات من API
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch("https://nspo-backend-gray.vercel.app/api/jobs");

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // ✅ فلترة الوظائف التي لم تنتهِ بعد
        const today = new Date();
        const activeJobs = data.filter((job) => new Date(job.endDate) >= today);

        setJobs(activeJobs);
      } catch (err) {
        console.error("Error fetching jobs:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  // ✅ استخراج القيم الفريدة
  const departments = useMemo(() => {
    if (!jobs || jobs.length === 0) return [];
    return [...new Set(jobs.map((job) => job.company || t("common.unknown")))];
  }, [jobs, t]);

  const locations = useMemo(() => {
    if (!jobs || jobs.length === 0) return [];
    return [...new Set(jobs.map((job) => job.location || t("common.unknown")))];
  }, [jobs, t]);

  // ✅ فلترة الوظائف
  const filteredJobs = useMemo(() => {
    if (!jobs || jobs.length === 0) return [];

    return jobs.filter((job) => {
      const title = job.title || "";
      const company = job.company || "";
      const location = job.location || "";
      const description = job.description || "";
      const requirements = job.requirements?.join(" ") || "";

      const matchesSearch =
        !searchTerm ||
        title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.toLowerCase().includes(searchTerm.toLowerCase()) ||
        description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        requirements.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesDepartment =
        departmentFilter === "all" || company === departmentFilter;

      const matchesLocation =
        locationFilter === "all" || location === locationFilter;

      return matchesSearch && matchesDepartment && matchesLocation;
    });
  }, [jobs, searchTerm, departmentFilter, locationFilter]);

  // ✅ إعداد الخيارات
  const departmentOptions = useMemo(() => {
    return [
      { value: "all", label: t("buttons.filters.all_departments") },
      ...departments.map((dept) => ({ value: dept, label: dept })),
    ];
  }, [departments, t]);

  const locationOptions = useMemo(() => {
    return [
      { value: "all", label: t("buttons.filters.all_locations") },
      ...locations.map((loc) => ({ value: loc, label: loc })),
    ];
  }, [locations, t]);

  // ✅ حالة التحميل
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-700 font-medium">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  // ✅ حالة الخطأ
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t("common.error_occurred")}
          </h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button
            onClick={() => window.location.reload()}
            className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-medium rounded-lg px-5 py-2"
          >
            {t("buttons.retry")}
          </Button>
        </div>
      </div>
    );
  }

  // ✅ عرض الصفحة الرئيسية
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-40 bg-gradient-to-b from-white to-blue-50 dark:from-gray-900 dark:to-gray-800">
      {/* ✅ العنوان */}
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
          {t("nav.careers")}
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
          {t("careers.subtitle")}
        </p>
      </div>

      {/* ✅ filters */}
      <div className="mb-10 flex flex-col md:flex-row gap-4 items-center justify-center bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-md">
        <div className="relative w-full md:w-1/3">
          <Input
            placeholder={t("buttons.search")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-gray-300 focus:border-blue-500"
          />
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-blue-500" />
        </div>

        <Select
          options={departmentOptions}
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
          className="md:w-48"
        />

        <Button
          variant="outline"
          onClick={() => {
            setSearchTerm("");
            setDepartmentFilter("all");
            setLocationFilter("all");
          }}
          className="flex items-center bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-4 py-2 rounded-lg"
        >
          <FunnelIcon className="h-4 w-4 mr-2" />
          {t("buttons.reset")}
        </Button>
      </div>

      {/* ✅ عرض الوظائف */}
      <div className="grid grid-cols-1 gap-6">
        {filteredJobs.map((job) => (
          <Card
            key={job._id}
            className="group hover:shadow-xl hover:scale-[1.01] transition-all duration-300 border border-blue-100 rounded-2xl bg-white dark:bg-gray-800"
          >
            <Card.Content className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="flex-1">
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 transition-colors">
                    {job.title || t("careers.no_title")}
                  </h3>

                  <div className="flex flex-wrap gap-4 mb-4 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-1 text-blue-600">
                      <BuildingOfficeIcon className="h-4 w-4" />
                      {job.company || t("common.unknown")}
                    </div>
                    {job.location && (
                      <div className="flex items-center gap-1 text-cyan-600">
                        <MapPinIcon className="h-4 w-4" />
                        {job.location}
                      </div>
                    )}
                    <div className="flex items-center gap-1 text-gray-500">
                      <CalendarIcon className="h-4 w-4" />
                      {t("تاريخ انتهاء الاعلان")}:{" "}
                      {job.endDate
                        ? new Date(job.endDate).toLocaleDateString(currentLang)
                        : t("common.not_specified")}
                    </div>
                  </div>

                  <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                    {job.description || t("careers.no_description")}
                  </p>

                  {job.requirements && job.requirements.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                        متطلبات الوظيفة :
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {job.requirements.map((requirement, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                          >
                            {requirement}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 lg:mt-0 lg:ml-6">
                  <Link
                    to={`/careers/${job._id}`}
                    className="inline-block px-6 py-2 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white rounded-xl transition-all duration-300 font-medium"
                  >
                    {t("buttons.apply_now")}
                  </Link>
                </div>
              </div>
            </Card.Content>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Careers;
